import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAccess } from "@/contexts/AccessContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  type AnsichtWahl,
  NEUE_ANSICHT_KEY,
  UMZUG_FUER_ALLE,
  ansichtAktiv,
  ansichtAktivNachUmzug,
  einladungIn,
  festAusMetadaten,
  istUebernahme,
  sollServerFragen,
  wahlAbonnieren,
  wahlLesen,
  wahlSetzen,
} from "@/lib/labs/neueAnsicht";
import { zertifikatLinkMerken } from "@/lib/labs/zertifikatLink";

export interface NeueAnsichtStand {
  /** Darf dieses Konto den Schalter sehen? (Antwort des Servers) */
  darf: boolean;
  /** Zeichnet `StudentLayout` gerade die neue Hülle? */
  aktiv: boolean;
  waehlen: (wahl: AnsichtWahl) => void;
}

/**
 * Schalter „Neue Ansicht“ (Etappe 8, 4a). Regeln in `src/lib/labs/neueAnsicht.ts`.
 *
 * Wird in `StudentLayout` aufgerufen, also auf jeder Seite des Mitgliederbereichs.
 * Deshalb hier nichts, was für Mitglieder eine Abfrage auslöst: Das Konto kommt
 * vom Aufrufer (dort ist die Sitzung schon bekannt), `useAccess` läuft ohnehin,
 * der `localStorage` ist lokal. Gefragt wird nur
 * `labs_funktion_freigegeben('neue_ansicht')`, und nur bei Admin-Rolle,
 * gespeicherter Wahl oder Einladung.
 *
 * Nach dem Umzug (`UMZUG_FUER_ALLE`) fragt jedes angemeldete Konto einmal je Sitzung,
 * und die Antwort des Servers entscheidet allein (Not-Aus über die Freigabe); eine
 * eigene Wahl gibt es dann nicht mehr. Der Browser merkt sich nur die letzte Antwort.
 * Eine Übernahme zeigt dann ebenfalls die neue Ansicht (Frage 284: die Administration
 * sieht, was das Mitglied sieht), merkt sich aber nichts: Der `localStorage` gehört
 * dem Browser der Administration. Bis zum Umzug gilt in einer Übernahme die gewohnte.
 *
 * Schalter im Admin (Sitzung N): Trägt das Token `neue_ansicht: "fest"`, gilt das wie eine Einladung bei jedem Laden,
 * ohne Wahl und ohne Weg zurück. Gelesen aus `useCurrentUser()` (die Sitzung im Browser), also ohne Abfrage.
 */
export function useNeueAnsicht(kontoId: string | null | undefined, umzug: boolean = UMZUG_FUER_ALLE): NeueAnsichtStand {
  const { isAdmin } = useAccess();
  const { search, pathname } = useLocation();
  const userId = kontoId ?? null;
  const uebernahme = istUebernahme();
  const { data: konto } = useCurrentUser();
  const fest = !!userId && konto?.id === userId && festAusMetadaten(konto?.app_metadata);
  const einladung = !umzug && !uebernahme && (einladungIn(search) || fest);

  const gespeichert = useSyncExternalStore(
    wahlAbonnieren,
    () => wahlLesen(userId),
    () => null,
  );
  // Eine Einladung gilt sofort als Wahl „neu“; gespeichert wird sie unten.
  const wahl: AnsichtWahl | null = uebernahme ? null : einladung ? "neu" : gespeichert;
  // `?zertifikat=` merken, bevor die alte Seite darunter ihn entfernt (src/lib/labs/zertifikatLink.ts).
  // Ohne Bedingung an die Wahl: Beim ersten Rendern kennt `StudentLayout` das Konto noch nicht.
  // Nur ein Vergleich der Adresse, keine Abfrage; gelesen wird der Link nur von der neuen Seite.
  if (umzug || !uebernahme) zertifikatLinkMerken(pathname, search);

  const fragen = !!userId && (umzug || (!uebernahme && sollServerFragen({ istAdmin: isAdmin, wahl, einladung })));
  const freigabe = useQuery({
    queryKey: ["labs-neue-ansicht", userId],
    enabled: fragen,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("labs_funktion_freigegeben", { _key: NEUE_ANSICHT_KEY });
      if (error) throw error;
      return data === true;
    },
  });

  // Nicht gegen ein Nein des Servers speichern, sonst schreiben dieser und der nächste Effekt sich gegenseitig
  // (etwa beim Schalter im Admin, wenn er aus ist, das Token aber noch das Merkmal trägt).
  useEffect(() => {
    if (userId && einladung && gespeichert !== "neu" && freigabe.data !== false) wahlSetzen(userId, "neu");
  }, [userId, einladung, gespeichert, freigabe.data]);

  // Vor dem Umzug: Sagt der Server nein, fällt die Wahl weg, danach fragt dieser
  // Browser für dieses Konto nicht mehr (z. B. nach dem Ende einer Testgruppe).
  // Nach dem Umzug: Der Browser merkt sich die letzte Antwort, damit beim nächsten
  // Laden nichts aufblitzt, auch nach einem Not-Aus.
  useEffect(() => {
    if (!userId || uebernahme || freigabe.data === undefined) return;
    if (umzug) {
      const soll: AnsichtWahl = freigabe.data ? "neu" : "gewohnt";
      if (gespeichert !== soll) wahlSetzen(userId, soll);
      return;
    }
    if (freigabe.data === false && gespeichert !== null) wahlSetzen(userId, null);
  }, [userId, uebernahme, umzug, freigabe.data, gespeichert]);

  const waehlen = useCallback(
    (neueWahl: AnsichtWahl) => {
      if (userId) wahlSetzen(userId, neueWahl);
    },
    [userId],
  );

  if (umzug) {
    // Kein Schalter mehr, in keiner Richtung.
    const gemerkt = uebernahme ? null : gespeichert;
    return { darf: false, aktiv: fragen && ansichtAktivNachUmzug({ gemerkt, freigabe: freigabe.data }), waehlen };
  }
  return {
    darf: fragen && freigabe.data === true,
    aktiv: fragen && ansichtAktiv({ wahl, freigabe: freigabe.data, fehler: freigabe.isError }),
    waehlen,
  };
}
